import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { CreditCard, TrendingUp } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';

const Overdraft = () => {
  const [overdraft, setOverdraft] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverdraft();
  }, []);

  const fetchOverdraft = async () => {
    try {
      const response = await api.get('/me/credit/overdraft');
      setOverdraft(response.data);
    } catch (error) {
      console.error('Error fetching overdraft:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Cheque Especial</h1>
          <p className="text-muted-foreground mt-2">Seu limite de crédito</p>
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!overdraft) {
    return (
      <div className="space-y-8" data-testid="overdraft-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Cheque Especial</h1>
          <p className="text-muted-foreground mt-2">Seu limite de crédito</p>
        </div>
        <Card className="border-border/50">
          <CardContent className="py-12 text-center">
            <CreditCard className="mx-auto h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground">Você ainda não possui limite de cheque especial.</p>
            <p className="text-sm text-muted-foreground mt-2">Entre em contato com seu gerente.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const usagePercentage = 0;

  return (
    <div className="space-y-8" data-testid="overdraft-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Cheque Especial</h1>
        <p className="text-muted-foreground mt-2">Seu limite de crédito</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="border-border/50 bg-gradient-to-br from-accent/10 to-background">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" strokeWidth={1.5} />
              Limite Total
            </CardTitle>
            <CardDescription>Valor disponível para uso</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold font-mono tracking-tight" data-testid="overdraft-total">
              {formatCurrency(overdraft.limit_total)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" strokeWidth={1.5} />
              Taxa de Juros
            </CardTitle>
            <CardDescription>Cobrada mensalmente</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold font-mono tracking-tight">
              {(overdraft.interest_rate_monthly * 100).toFixed(2)}%
            </div>
            <p className="text-sm text-muted-foreground mt-2">ao mês</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle>Utilização do Limite</CardTitle>
          <CardDescription>Acompanhe quanto do seu limite está sendo usado</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Utilizado</span>
              <span className="font-bold font-mono">{usagePercentage.toFixed(1)}%</span>
            </div>
            <Progress value={usagePercentage} className="h-3" />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border/50 p-4">
              <p className="text-sm text-muted-foreground mb-2">Limite Disponível</p>
              <p className="text-2xl font-bold font-mono text-success">
                {formatCurrency(overdraft.limit_total)}
              </p>
            </div>
            <div className="rounded-lg border border-border/50 p-4">
              <p className="text-sm text-muted-foreground mb-2">Limite Usado</p>
              <p className="text-2xl font-bold font-mono">
                {formatCurrency(0)}
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 p-4">
            <p className="text-sm text-muted-foreground mb-2">Status</p>
            <p className="font-medium text-success">
              {overdraft.status === 'ACTIVE' ? 'Ativo' : 'Suspenso'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Overdraft;